/* eslint-disable max-classes-per-file */
import { IAggregatPeriod } from "~app/common/models/aggregatperiod";
import { IDagKvantitet } from "~app/common/models/dagkvantitet";
import { ForsorjningskallaAggregat } from "~app/common/models/forsorjningskalla";
import { LeveransraderAggregat } from "~app/common/models/leveransrad";
import { TaktCalculator } from "../../../../common/models/takt-calculator";
import { IOrderHandelssortiment, IOrderKundorderrad, IOrderMottagningsplats } from "./justeraLeveransplanModel";

export class KundorderAggregat {
    kundorderrader: KundorderradAggregat[];

    constructor(raw: IOrderKundorderrad[]) {
        this.kundorderrader = raw.map(x => new KundorderradAggregat(x));
    }

    sumLeveransrad = (period: IAggregatPeriod): number => this.kundorderrader.reduce((agg, item) => agg + item.sumLeveransrad(period), 0);

    sumTransporterat = (period: IAggregatPeriod): number => this.kundorderrader.reduce((agg, item) => agg + item.sumTransporterat(period), 0);

    sumTransporteratLeveransrad = (periods: IAggregatPeriod[]): number => {
        const actives = periods.filter(x => x.isCurrentlyActive);
        let planerat = 0;
        actives.forEach(x => {
            planerat += this.sumLeveransrad(x);
        });

        const nonActives = periods.filter(x => !x.isCurrentlyActive);
        let transporterat = 0;
        nonActives.forEach(x => {
            transporterat += this.sumTransporterat(x);
        });
        return planerat + transporterat;
    };

    sumPlan = (period: IAggregatPeriod): number => this.kundorderrader.reduce((agg, item) => agg + item.sumPlan(period), 0);

    sumBalans = (period: IAggregatPeriod): number => this.kundorderrader.reduce((agg, item) => agg + item.sumBalans(period), 0);

    sumOverenskommet = (): number => this.kundorderrader.reduce((agg, item) => agg + item.overenskommet, 0);

    sumAvtalad = (period: IAggregatPeriod): number => this.kundorderrader.reduce((agg, item) => agg + item.sumAvtalat(period), 0);

    calculateTakt = (period: IAggregatPeriod, passeradeArbetsdagar: number, antalArbetsdagarInnevarandeManad: number): number =>
        TaktCalculator.calculate(period, this.sumPlan, this.sumTransporterat, passeradeArbetsdagar, antalArbetsdagarInnevarandeManad);

    calculateTaktLeverans = (period: IAggregatPeriod, passeradeArbetsdagar: number, antalArbetsdagarInnevarandeManad: number): number =>
        TaktCalculator.calculate(period, this.sumLeveransrad, this.sumTransporterat, passeradeArbetsdagar, antalArbetsdagarInnevarandeManad);
}

export class KundorderradAggregat {
    get kundorderId(): number {
        return this.kor.id;
    }

    get kundorderOrdernummer(): string {
        return this.kor.ordernummer;
    }

    get kundorderradId(): number {
        return this.kor.kundorderrad.id;
    }

    get kommentar(): string {
        return this.kor.kommentar;
    }

    get kundId(): number {
        if (!this.kor.kund) {
            return null;
        }
        return this.kor.kund.id;
    }

    get kundNamn(): string {
        if (!this.kor.kund) {
            return "";
        }
        return this.kor.kund.namn;
    }

    get mattslag(): string {
        return this.kor.mattslag;
    }

    get mottagningsplats(): IOrderMottagningsplats {
        return this.kor.kundorderrad.mottagningsplats;
    }

    get handelssortiment(): IOrderHandelssortiment {
        return this.kor.kundorderrad.handelssortiment;
    }

    get overenskommet(): number {
        return this.kor.kundorderrad.overenskommenKvantitetLeveransrad;
    }

    set overenskommet(value: number) {
        this.kor.kundorderrad.overenskommenKvantitetLeveransrad = value;
    }

    get periodFrom(): Date {
        return this.kor.kundorderrad.periodFrom;
    }

    get periodTom(): Date {
        return this.kor.kundorderrad.periodTom;
    }

    readonly forsorjningskallor: ForsorjningskallaAggregat[];

    private readonly leveransraderAggregat: LeveransraderAggregat;

    constructor(private readonly kor: IOrderKundorderrad) {
        this.leveransraderAggregat = new LeveransraderAggregat(kor.kundorderrad.leveransrader);
        this.forsorjningskallor = ForsorjningskallaAggregat.createMany(kor.kundorderrad.forsorjningskallor, this.leveransraderAggregat);
    }

    // eslint-disable-next-line max-len
    sumTransporteratForsorjningskallor = (period: IAggregatPeriod, forsorjningskallorIdn?: number[]): number => this.leveransraderAggregat.sumTransporterat(period, forsorjningskallorIdn);

    sumLeveransrad = (period: IAggregatPeriod): number => this.leveransraderAggregat.sumLeveransrad(period);

    sumTransporterat = (period: IAggregatPeriod): number => this.leveransraderAggregat.sumTransporteratLeveransrad(period);

    sumAvtalat = (period: IAggregatPeriod): number => this.leveransraderAggregat.sumAvtalatLeveransrad(period);

    sumPlan = (period: IAggregatPeriod): number => this.forsorjningskallor.reduce((agg, item) => agg + item.sumPlan(period), 0);

    sumBalans = (period: IAggregatPeriod): number => {
        if (!period.isCurrentlyActive) {
            return this.sumTransporterat(period) - this.sumLeveransrad(period);
        }

        return this.sumPlan(period) - this.sumLeveransrad(period);
    };

    sumTransporteratLeveransrad = (periods: IAggregatPeriod[]): number => {
        const actives = periods.filter(x => x.isCurrentlyActive);
        let planerat = 0;
        actives.forEach(x => {
            planerat += this.sumLeveransrad(x);
        });

        const nonActives = periods.filter(x => !x.isCurrentlyActive);
        let transporterat = 0;
        nonActives.forEach(x => {
            transporterat += this.sumTransporterat(x);
        });
        return planerat + transporterat;
    };

    avvikelse = (periods: IAggregatPeriod[]): number => this.sumTransporteratLeveransrad(periods) - this.overenskommet;

    calculateTaktLeverans = (period: IAggregatPeriod, passeradeArbetsdagar: number, antalArbetsdagarInnevarandeManad: number): number =>
        TaktCalculator.calculate(period, this.sumLeveransrad, this.sumTransporterat, passeradeArbetsdagar, antalArbetsdagarInnevarandeManad);

    updateLeveransradKvantitet(fordeladKvantitet: IDagKvantitet[]): void {
        this.leveransraderAggregat.updateLeveransradKvantitet(fordeladKvantitet);
    }
}

export class JlpKundorderSearchModel {
    kunder?: any[];
    omraden?: any[];
    mottagningsplatser?: any[];
    handelssortimentsgrupper?: any[];
    periodFrom: Date;
    periodTom: Date;

    constructor() {
        this.kunder = [];
        this.omraden = [];
        this.mottagningsplatser = [];
        this.handelssortimentsgrupper = [];
        this.periodFrom = null;
        this.periodTom = null;
    }
}
